/**
 * „Speicher für Bewohner" — was ein Speicher den Wohnungen zugesteht (HM-172,
 * ADR 0050). Auf der Detailseite eines Speichers, mit Einstelldialog.
 *
 * Je Stufe ab einem Speicherstand: wie viel der Speicher an die Wohnungen
 * abgeben darf und wie viel der PV-Leistung, die gerade in ihn geht, für das
 * Laden zurückbleibt. Das bestimmt, was „Dein Strom vom Haus" als frei zeigt —
 * **gesteuert wird der Speicher nicht**, und der Abschnitt sagt das.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Checkbox, Dialog, IconButton, Input, icons } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { useState } from "react";
import { Section } from "./section.js";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import {
  bandText,
  DEFAULT_POLICY,
  draftOf,
  MAX_BANDS,
  policyOf,
  type DraftBand,
  type StorageTenantPolicy,
} from "../lib/storage-tenant-policy.js";

type GenerationAsset = ApiComponents["schemas"]["GenerationAsset"];

export function StorageTenantPolicySection({
  asset,
  canWrite,
}: {
  readonly asset: GenerationAsset;
  readonly canWrite: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const policy = asset.tenantPolicy;

  return (
    <Section
      title="Speicher für Bewohner"
      subtitle="Wie viel davon „Dein Strom vom Haus“ den Wohnungen als frei zeigt — je Speicherstand."
      action={
        canWrite ? (
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            Einstellen …
          </Button>
        ) : undefined
      }
    >
      <Card>
        {policy === null ? (
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
            Keine Regel: Der Speicher zählt für die Wohnungen nicht mit. Frei ist für sie nur, was das Haus
            gerade ins Netz einspeist.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <BandTable policy={policy} />
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
              Bewohner sehen nur, was frei ist — nicht den Speicherstand und nicht die Erzeugung. Die Regel
              bestimmt die Anzeige; den Speicher steuert sie nicht.
            </p>
          </div>
        )}
      </Card>
      {editing && <PolicyDialog asset={asset} onClose={() => setEditing(false)} />}
    </Section>
  );
}

function BandTable({ policy }: { readonly policy: StorageTenantPolicy }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ color: "var(--text-faint)", fontSize: 11.5, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          <th style={{ textAlign: "left", fontWeight: 700, padding: "0 0 6px" }}>Speicherstand</th>
          <th style={{ textAlign: "left", fontWeight: 700, padding: "0 0 6px" }}>Abgabe an Wohnungen</th>
          <th style={{ textAlign: "left", fontWeight: 700, padding: "0 0 6px" }}>Ladereserve aus PV</th>
        </tr>
      </thead>
      <tbody>
        {policy.bands.map((band, index) => {
          const text = bandText(band);
          const next = policy.bands[index + 1];
          return (
            <tr key={band.fromSocPercent} style={{ borderTop: "1px solid var(--border-subtle)" }}>
              <td style={{ padding: "8px 8px 8px 0", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>
                {next === undefined ? `ab ${band.fromSocPercent} %` : `${band.fromSocPercent}–${next.fromSocPercent} %`}
              </td>
              <td style={{ padding: "8px 8px 8px 0", color: "var(--text-strong)" }}>{text.discharge}</td>
              <td style={{ padding: "8px 0", color: "var(--text-strong)" }}>{text.reserve}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PolicyDialog({ asset, onClose }: { readonly asset: GenerationAsset; readonly onClose: () => void }) {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<DraftBand[]>(() => draftOf(asset.tenantPolicy ?? DEFAULT_POLICY));
  const [tried, setTried] = useState(false);
  const { policy, errors } = policyOf(rows);

  const save = useMutation({
    mutationFn: async (next: StorageTenantPolicy | null) => {
      const { data, error } = await api.PUT("/generation-assets/{generationAssetId}/tenant-policy", {
        params: { path: { generationAssetId: asset.id } },
        body: { policy: next },
      });
      if (error || !data) throw new Error(apiErrorMessage(error) ?? "Speichern fehlgeschlagen.");
      return data;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["generation-assets", "detail", asset.id], updated);
      void queryClient.invalidateQueries({ queryKey: ["generation-assets", asset.siteId] });
      onClose();
    },
  });

  const update = (index: number, change: Partial<DraftBand>): void =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...change } : row)));

  return (
    <Dialog
      open
      onClose={onClose}
      title="Speicher für Bewohner"
      description="Ab welchem Speicherstand der Speicher wie viel an die Wohnungen abgeben darf — und wie viel PV-Leistung er zum Laden behält."
      width={720}
      footer={
        <>
          {asset.tenantPolicy !== null && (
            <Button variant="ghost" onClick={() => save.mutate(null)} disabled={save.isPending} style={{ marginRight: "auto" }}>
              Regel entfernen
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            disabled={save.isPending}
            onClick={() => {
              setTried(true);
              if (policy !== null) save.mutate(policy);
            }}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,0.8fr) minmax(0,1fr) minmax(0,1.7fr) 36px",
            gap: 10,
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--text-faint)",
          }}
        >
          <span>ab Stand (%)</span>
          <span>Abgabe (kW)</span>
          <span>Ladereserve aus PV (kW)</span>
          <span />
        </div>
        {rows.map((row, index) => (
          <div key={index} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,0.8fr) minmax(0,1fr) minmax(0,1.7fr) 36px",
                gap: 10,
                alignItems: "center",
              }}
            >
              <Input
                label={`Stufe ${index + 1}: ab Speicherstand in Prozent`}
                labelHidden
                size="sm"
                inputMode="numeric"
                value={row.from}
                disabled={index === 0}
                onChange={(event) => update(index, { from: event.target.value })}
              />
              <Input
                label={`Stufe ${index + 1}: Abgabe an die Wohnungen in kW`}
                labelHidden
                size="sm"
                inputMode="decimal"
                value={row.dischargeKw}
                onChange={(event) => update(index, { dischargeKw: event.target.value })}
              />
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, whiteSpace: "nowrap" }}>
                <Input
                  label={`Stufe ${index + 1}: Ladereserve in kW`}
                  labelHidden
                  size="sm"
                  inputMode="decimal"
                  value={row.reserveAll ? "" : row.reserveKw}
                  placeholder={row.reserveAll ? "alles" : undefined}
                  disabled={row.reserveAll}
                  onChange={(event) => update(index, { reserveKw: event.target.value })}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <Checkbox checked={row.reserveAll} onChange={(checked) => update(index, { reserveAll: checked })} label="alles" />
              </div>
              {index === 0 ? (
                <span />
              ) : (
                <IconButton
                  label={`Stufe ${index + 1} entfernen`}
                  variant="ghost"
                  size="sm"
                  onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
                >
                  <icons.IconClose size={14} />
                </IconButton>
              )}
            </div>
            {tried && errors[index] !== undefined && (
              <span style={{ fontSize: 12, color: "var(--hm-danger)" }}>{errors[index]}</span>
            )}
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {rows.length < MAX_BANDS && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const last = Number(rows[rows.length - 1]?.from ?? "0");
                setRows((current) => [
                  ...current,
                  { from: String(Math.min(100, (Number.isFinite(last) ? last : 0) + 10)), dischargeKw: "0", reserveAll: false, reserveKw: "0" },
                ]);
              }}
            >
              + Stufe
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setRows(draftOf(DEFAULT_POLICY))}>
            Vorgabe übernehmen
          </Button>
        </div>
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
          Frei für die Wohnungen ist dann: was das Haus einspeist, dazu was der Speicher gerade aus PV lädt
          abzüglich der Ladereserve, dazu was er bis zur Abgabe noch hergeben darf. „alles“ heißt: Was er lädt,
          ist nicht frei. Die Regel gilt nur bei genau einem Speicher an der Anlage und steuert ihn nicht.
        </p>
        {tried && errors[-1] !== undefined && <Alert tone="danger">{errors[-1]}</Alert>}
        {save.isError && <Alert tone="danger">{save.error.message}</Alert>}
      </div>
    </Dialog>
  );
}
